import React, { useContext, useState } from 'react';
import { ColumnLayout, DatePicker, FormField, Input, Link, Select, SpaceBetween } from '@amzn/awsui-components-react/polaris';
import CONSTANTS from 'src/utils/constants';
import { PeriodType, SegmentPullCriteria } from 'src/models/segment-cdt/SegmentCdtTypes';
import { SegmentTablesContext } from 'src/components/segment-cdt/SegmentTablesContext';
import {
    fromCustomPeriodDisplay,
    periodLabel,
    periodOptions,
    toCustomPeriodDisplay,
} from 'src/components/segment-cdt/create/segmentCreateUtils';
import { CustomPeriodModal } from 'src/components/calculation-builder/data-sources/CustomPeriodModal';
import CurrencyAutosuggest from 'src/components/segment-cdt/create/CurrencyAutosuggest';

/**
 * The scalar GL-pull settings a column carries: period, currency, FX type, FX date and a free-text note.
 *
 * Shared by both forms that configure a GL pull — {@link GlPullColumnForm} for a `PULL_GL` column and
 * `DerivedColumnForm`'s balance flavour, whose aggregate is a GL pull with the same criteria. They previously
 * duplicated the period and currency fields, and adding three more to each would have doubled that: the two
 * would then have been free to drift in wording, ordering and which fields they offered, and a derived balance
 * would have been the only GL pull that could not name an FX conversion.
 *
 * Deliberately excludes the ledger and the COA data keys, which stay in {@link SegmentGlPullDataSource} because
 * they are driven by a contained calculation-builder reducer rather than by the draft. And deliberately
 * excludes the **FX rate**: that is the rate the pull applied, reported per column on the pull's status, not
 * something a caller configures. The CLI datasource grid treats it the same way, as a read-only cell.
 *
 * Currency is on the column itself and the rest are on its `pullCriteria`, hence the two separate callbacks
 * rather than one patch.
 */
export default function GlPullCriteriaFields(props: {
    criteria: SegmentPullCriteria;
    currency?: string;
    onCriteriaChange: (patch: Partial<SegmentPullCriteria>) => void;
    onCurrencyChange: (currency: string) => void;
}) {
    const { criteria: pc, currency, onCriteriaChange, onCurrencyChange } = props;
    const { services } = useContext(SegmentTablesContext);

    // Both LOVs ignore the query server-side and return the full list, and the hooks skip the fetch entirely
    // for an empty one — so a stable non-empty value loads each once. `fxType` is validated server-side against
    // this exact list of display-cased strings ('Period Average'), which is why it is fetched rather than
    // declared as an enum: a SCREAMING_CASE vocabulary would not match and would be refused on save.
    const [currencyOptions] = services.customDataTablesService.getCurrencyCodeLOV('load');
    const [fxTypes, fxTypesLoading] = services.customDataTablesService.getCurrencyConversionTypeLOV('load');

    // The same period list a CLI data source offers, from GET /lov/periods. The arrears entries appear only when
    // the Secondary Ledger flag is on, because the endpoint filters them, exactly as on the CLI form.
    const [lovPeriods, periodsLoading, periodsError] = services.dataSourcesService.getPeriodTypes('load');
    const fetchedPeriods = periodOptions(lovPeriods);
    // Keep the chosen period selectable while the list loads, so a remount (Back) does not render it as unset.
    const periodChoices = pc.periodType && !fetchedPeriods.some((o) => o.value === pc.periodType)
        ? [{ value: pc.periodType, label: periodLabel(pc.periodType) }, ...fetchedPeriods]
        : fetchedPeriods;

    // Custom Period reuses the CLI data source's month-range modal, so the two collect a range the same way.
    const [showCustomPeriod, setShowCustomPeriod] = useState(false);
    const customStart = toCustomPeriodDisplay(pc.startPeriod);
    const customEnd = toCustomPeriodDisplay(pc.endPeriod);
    const onPeriodChange = (value: PeriodType) => {
        if (value === PeriodType.CUSTOM_PERIOD) {
            onCriteriaChange({ periodType: value });
            setShowCustomPeriod(true);
        } else {
            // Clear any range: it is only valid with Custom Period, and the server refuses a stray one.
            onCriteriaChange({ periodType: value, startPeriod: undefined, endPeriod: undefined });
        }
    };

    // A Clear entry first, because FX type is optional and a Select otherwise offers no way back to unset once
    // a value has been picked. The CLI datasource grid heads the same list the same way.
    const fxTypeOptions = [
        { value: CONSTANTS.CLEAR_SELECTION, label: CONSTANTS.CLEAR_SELECTION },
        ...(fxTypes ?? []).map((type) => ({ value: type, label: type })),
    ];

    return (
        <SpaceBetween size="l">
            <ColumnLayout columns={2}>
                <FormField
                    label="Period"
                    // Below the control rather than as a description, so Period and Currency stay on one baseline.
                    constraintText={pc.periodType === PeriodType.CUSTOM_PERIOD ? (
                        <span>
                            {customStart && customEnd ? `${customStart} - ${customEnd}` : 'No range chosen'}{' '}
                            <Link variant="secondary" onFollow={() => setShowCustomPeriod(true)}>Change range</Link>
                        </span>
                    ) : undefined}
                >
                    <Select
                        selectedOption={periodChoices.find((o) => o.value === pc.periodType) ?? null}
                        options={periodChoices}
                        placeholder="Select a period"
                        statusType={periodsError ? 'error' : periodsLoading ? 'loading' : 'finished'}
                        loadingText="Loading periods"
                        errorText="Could not load the periods."
                        empty="No periods are available."
                        onChange={({ detail }) => onPeriodChange(detail.selectedOption.value as PeriodType)}
                    />
                </FormField>
                <FormField label="Currency">
                    <CurrencyAutosuggest
                        options={currencyOptions ?? []}
                        value={currency ?? ''}
                        onChange={onCurrencyChange}
                    />
                </FormField>
                {/* Both descriptions are kept to a single line on purpose: this is a two-column layout, and a
                    wrapping description pushes its control down so the FX type and FX date inputs no longer
                    share a baseline.

                    They also say what absent actually means. Leaving these unset does NOT pull unconverted --
                    CDTGLPullComponent simply omits fxType/fxDate from the PullBalanceRequest, and the GL
                    Balance service then applies its own defaults, which are Period-Average and period-end
                    (see CR-304191350, the shipped consumer). An earlier draft said "leave unset to pull
                    unconverted", which was wrong. */}
                <FormField
                    label="FX type"
                    description="Optional. Defaults to Period Average."
                >
                    <Select
                        filteringType="auto"
                        selectedOption={fxTypeOptions.find((o) => o.value === pc.fxType) ?? null}
                        options={fxTypeOptions}
                        placeholder="Select an FX type"
                        statusType={fxTypesLoading ? 'loading' : 'finished'}
                        loadingText="Loading FX types"
                        /* Clear writes undefined rather than '', so buildPullCriteria omits the field: a cleared
                           FX type is then stored as absent rather than as an empty string, which the read-only
                           configuration popup would otherwise render as a blank row. */
                        onChange={({ detail }) =>
                            onCriteriaChange({
                                fxType: detail.selectedOption.value === CONSTANTS.CLEAR_SELECTION
                                    ? undefined
                                    : detail.selectedOption.value,
                            })
                        }
                    />
                </FormField>
                <FormField
                    label="FX date"
                    description="Optional. Defaults to the period end."
                >
                    {/* The same Cloudscape DatePicker the CLI datasource grid uses, so the value it emits is
                        already the YYYY-MM-DD the contract stores and the pull's consumer parses to the epoch
                        millis it needs. No conversion here, deliberately: a reformat is a chance to drift from
                        the one form the server accepts. */}
                    <DatePicker
                        value={pc.fxDate ?? ''}
                        placeholder="YYYY/MM/DD"
                        openCalendarAriaLabel={(selected) =>
                            'Choose FX date' + (selected ? `, selected date is ${selected}` : '')
                        }
                        previousMonthAriaLabel="Previous month"
                        nextMonthAriaLabel="Next month"
                        todayAriaLabel="Today"
                        onChange={({ detail }) => onCriteriaChange({ fxDate: detail.value || undefined })}
                    />
                </FormField>
            </ColumnLayout>

            <FormField
                label="Description"
                description="Optional. A note carried with the column."
            >
                <Input
                    value={pc.description ?? ''}
                    placeholder="e.g. Opex headcount pull for EMEA cost centres"
                    onChange={({ detail }) => onCriteriaChange({ description: detail.value || undefined })}
                />
            </FormField>
            <CustomPeriodModal
                visible={showCustomPeriod}
                onClose={() => setShowCustomPeriod(false)}
                // The modal works in MMM/YYYY, the contract in yyyyMM.
                recordBeingEdited={{ customPeriod: customStart && customEnd ? `${customStart} - ${customEnd}` : undefined } as any}
                onSubmit={(start, end) => {
                    setShowCustomPeriod(false);
                    onCriteriaChange({
                        startPeriod: fromCustomPeriodDisplay(start) ?? undefined,
                        endPeriod: fromCustomPeriodDisplay(end) ?? undefined,
                    });
                }}
            />
        </SpaceBetween>
    );
}
